// Stap 5: email-koppeling. PRD-06 §4 US-ONB-05.
// D-03: stap is verplicht, maar skip-knop blijft beschikbaar voor MVP-walkthrough.

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import type { OnboardingProgress } from "../OnboardingWizard";

interface Props {
  progress: OnboardingProgress;
  onBack: () => void;
  onAdvance: () => void;
  onSkip: () => void;
}

export function EmailConnectStep({ onBack, onAdvance, onSkip }: Props) {
  const { t } = useTranslation();

  const accountsQuery = useQuery({
    queryKey: ["email-accounts"],
    queryFn: () => api.get<{ accounts: Array<{ id: string; email: string }> }>("/v1/email-accounts"),
  });

  const hasAccount = (accountsQuery.data?.accounts.length ?? 0) > 0;

  return (
    <article className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
      <h2 className="text-2xl font-semibold mb-2">
        {t("onboarding.email.title", "E-mail koppelen")}
      </h2>
      <p className="text-neutral-700 mb-4">
        {t(
          "onboarding.email.intro",
          "We koppelen je e-mail zodat Ember brieven van crediteuren automatisch kan herkennen en categoriseren. We lezen niet mee in privé-correspondentie."
        )}
      </p>
      <p className="text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg text-sm mb-6">
        {t(
          "onboarding.email.required",
          "Voor het volledige BBZ-traject is e-mail-koppeling verplicht (D-03). Voor de MVP-walkthrough mag je deze stap overslaan."
        )}
      </p>

      {accountsQuery.isLoading ? (
        <p className="text-sm text-neutral-500">{t("loading", "Laden…")}</p>
      ) : hasAccount ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="font-medium text-green-900 mb-1">
            ✓ {t("onboarding.email.connected", "Gekoppeld")}
          </p>
          <p className="text-sm text-green-800">
            {accountsQuery.data!.accounts.map((a) => a.email).join(", ")}
          </p>
        </div>
      ) : (
        <Link
          to="/settings/email"
          className="block px-4 py-3 mb-6 bg-brand-500 hover:bg-brand-600 text-white text-center font-medium rounded-lg"
        >
          {t("onboarding.email.connect", "E-mailaccount koppelen →")}
        </Link>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2.5 text-neutral-700 hover:bg-neutral-100 rounded-lg"
        >
          ← {t("back", "Terug")}
        </button>
        {hasAccount ? (
          <button
            type="button"
            onClick={onAdvance}
            className="flex-1 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium"
          >
            {t("onboarding.continue", "Doorgaan")} →
          </button>
        ) : (
          <button
            type="button"
            onClick={onSkip}
            className="flex-1 px-4 py-2.5 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg"
          >
            {t("onboarding.skipForNow", "Sla over voor nu")} →
          </button>
        )}
      </div>
    </article>
  );
}
